import React, { useMemo } from 'react';
import * as d3 from 'd3';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { opacity } from 'html2canvas/dist/types/css/property-descriptors/opacity';
import ISONumber from 'components/formatting/ISONumber';
import { useTranslation } from 'react-i18next';

export interface NetTrendProps {
  topLabel: string;
  topValues: number[];

  bottomLabel: string;
  bottomValues: number[];

  xFieldLabel: string;
  xValues: string[];
  xLabels: string[];

  topColor: string;
  bottomColor: string;
  neutralColor: string;

  drawArea: boolean;
  drawLine: boolean;
  drawCircles: boolean;

  viewAsNetCount?: boolean;
  viewAsPortion?: boolean;

  hoveredIndex?: number;
  setHoveredIndex?: (index: number | undefined) => void;

  onClick?: (clickedIndex: number) => void;
}

interface NetTrendPathData {
  upperPath: string;
  upperArea: string;
  lowerPath: string;
  lowerArea: string;
};

export default function NetTrend(props: NetTrendProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const {
    topLabel, topValues,
    bottomLabel, bottomValues,
    xFieldLabel, xValues, xLabels,
    topColor, bottomColor, neutralColor,
    drawArea, drawLine, drawCircles,
    hoveredIndex, setHoveredIndex
  } = props;

  interface chartRow {
    x: number;
    y: number;
  }

  const contentArea = {
    width: 100,
    height: 100
  };

  const chartData = useMemo(() => {
    if (!topValues || !bottomValues || !xValues) {
      return null;
    }

    const xBounds = {max: topValues.length - 1, min: 0 },
      yBounds = {max: -Infinity, min: Infinity },
      pointValues:chartRow[] = [],
      lineValues:chartRow[] = [];

    let lastRow: chartRow | undefined = undefined,
      hasPositive = false,
      hasNegative = false;

    topValues.forEach((topValue: number, index: number) => {
      const bottomValue = bottomValues[index];
      const xValue = index;
      let innerXValue = index;

      const topAndBottom = topValue + bottomValue;

      let netValue = (topValue - bottomValue) / topAndBottom;
      if (!topAndBottom) {
        netValue = 0;
      } else {
        if (props.viewAsNetCount) {
          netValue = topValue - bottomValue;
        }
        if (props.viewAsPortion) {
          netValue = topValue / topAndBottom;
        }
      }

      // Bounds for chart data, faster this way than separate Math.max(...array) calls
      yBounds.max = Math.max(yBounds.max, netValue);
      yBounds.min = Math.min(yBounds.min, netValue);

      // Check if there is negative or positive chart data
      if (netValue >= 0) {
          hasPositive = true;
      }
      if (netValue < 0) {
          hasNegative = true;
      }

      // If line crosses the axis, add an intermediate point at
      // y = 0 and interpolated x to the lineValues
      if (lastRow && (lastRow?.x !== null && lastRow?.x !== undefined) &&
          (lastRow?.y !== null && lastRow?.y !== undefined)) {
          if ((lastRow.y > 0 && netValue < 0) ||
              (lastRow.y <= 0 && netValue > 0)) {
              const denom = (netValue - lastRow.y);
              if (denom) {
                  innerXValue = (netValue * lastRow.x - lastRow.y * xValue) / denom;
              } else {
                  innerXValue = 0.5 * (xValue - lastRow.x) + lastRow.x;
              }
              lineValues.push({ y: 0, x: innerXValue });
          }
      }

      lastRow = {
          y: netValue,
          x: xValue
      };

      lineValues.push(lastRow);
      pointValues.push(lastRow);
    }, []);
    yBounds.min = (yBounds.min > 0) ? 0 : yBounds.min;
    yBounds.max = (yBounds.max < 0) ? 0 : yBounds.max;

    return {
        lineValues: lineValues,
        pointValues: pointValues,
        xBounds: xBounds,
        yBounds: yBounds,
        hasPositive: hasPositive,
        hasNegative: hasNegative
    };
  }, [topValues, bottomValues, xValues]);

  const xScale = useMemo(() => {
    const xBounds = chartData?.xBounds;
    return d3.scaleLinear()
      .domain([xBounds?.min || 0, xBounds?.max || 10])
      .range([0, contentArea.width]);
  }, [chartData, contentArea.width]);

  const yScale = useMemo(() => {
    const height = contentArea.height;
    let max = chartData?.yBounds.max || 0;
    let min = chartData?.yBounds.min || 0;
    if (max === 0 && min === 0) {
      max = 1.0;
      min = -1.0;
    }
    return d3.scaleLinear()
      .domain([max, min])
      .range([2, contentArea.height - 2]);
  }, [chartData, contentArea.height]);

  const axisAreaHeight = useMemo(() => {
    return yScale ? yScale(0) : 0;
  }, [yScale]);

  const line = useMemo(() => {
    return d3.line()
      .x(d => xScale(d.x || 0))
      .y(d => yScale(d.y || 0))
      .defined(d => d.y >= 0)
      .curve(d3.curveMonotoneX);
  }, [xScale, yScale]);

  const area = useMemo(() => {
    return d3.area()
      .x(d => xScale(d.x || 0))
      .y0(yScale(0))
      .y1(d => yScale(d.y || 0))
      .defined(d => {return d.y >= 0;})
      .curve(d3.curveMonotoneX);
  }, [xScale, yScale]);

  const pathData = useMemo(() => {
    if (!chartData?.lineValues) {
      return null;
    }

    const paths:NetTrendPathData = {
        upperArea: 'M 0 0',
        lowerArea: 'M 0 0',
        upperPath: 'M 0 0',
        lowerPath: 'M 0 0'
      },
      lineValues = chartData?.lineValues;

    paths.upperPath = line(lineValues) || 'M 0 0';
    paths.upperArea = area(lineValues) || 'M 0 0';

    line.defined(d => { return d.y <= 0; });
    area.defined(d => { return d.y <= 0; });

    paths.lowerPath = line(lineValues) || 'M 0 0';
    paths.lowerArea = area(lineValues) || 'M 0 0';

    return paths;
  }, [chartData?.lineValues, line, area]);

  const pointCircles = useMemo(() => {
    return chartData?.pointValues?.map(row => {
      return {
        y: yScale(row.y),
        x: xScale(row.x),
        isPositive: row.y >= 0,
        color: row.y >= 0 ? topColor : bottomColor,
      };
    }) || [];
  }, [chartData?.pointValues, xScale, yScale]);

  const pathStyle = { strokeWidth: "2px", fill: "none" };
  const areaStyle = { strokeWidth: 0, stroke: "transparent", opacity: 0.5 };

  const hoveredValues = useMemo(() => {
    if (hoveredIndex === null || hoveredIndex === undefined) {
      return null;
    }
    return {
      hoveredLineX: (hoveredIndex / (xValues.length - 1)) * contentArea.width,
      topValue: topValues[hoveredIndex],
      bottomValue: bottomValues[hoveredIndex],
      hoveredXLabel: xLabels[hoveredIndex],
      net: ((topValues[hoveredIndex] - bottomValues[hoveredIndex]) / (topValues[hoveredIndex] + bottomValues[hoveredIndex])) * 100.0,
      portion: (topValues[hoveredIndex] / (topValues[hoveredIndex] + bottomValues[hoveredIndex])),
    };
  }, [hoveredIndex, topValues, bottomValues]);

  return (
  <Tooltip
    title={(hoveredIndex !== undefined && hoveredValues) ?
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr" }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal" }}>
          {i18n.t("net")}:
        </Typography>
        {hoveredValues && !isNaN(hoveredValues.net) ? (
          <ISONumber typographyVariant="h4" number={hoveredValues.net} sx={{ color: hoveredValues.net >= 0 ? topColor : bottomColor, m:0, p:0, ml: 2, textAlign:"right" }} postfix='%'/>
        ) : (
          <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal", textAlign: "right" }}>
            {i18n.t("widget-types.net-value-trend.no-data")}
          </Typography>
        )}
        <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal" }}>
          {topLabel}:
        </Typography>
        {hoveredValues.topValue && !isNaN(hoveredValues.topValue) ? (
           <ISONumber typographyVariant="h4" number={hoveredValues.topValue} sx={{ color: topColor, m:0, p:0, ml: 2, textAlign:"right" }} />
        ) : (
          <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal", textAlign: "right" }}>
            {i18n.t("widget-types.net-value-trend.no-data")}
          </Typography>
        )}
        <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal" }}>
          {bottomLabel}:
        </Typography>
        {hoveredValues.bottomValue && !isNaN(hoveredValues.bottomValue) ? (
          <ISONumber typographyVariant="h4" number={hoveredValues.bottomValue} sx={{ color: bottomColor, m:0, p:0, ml: 2, textAlign:"right" }} />
        ) : ( 
          <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal", textAlign: "right" }}>
            {i18n.t("widget-types.net-value-trend.no-data")}
          </Typography>
        )}
        <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal" }}>
          {xFieldLabel}:
        </Typography>
        <Typography variant="h4" sx={{ mb: 1, ml: 2, fontWeight: "bold", color: theme.colors.primary.main, textAlign:"right" }}>
          {hoveredValues?.hoveredXLabel}
        </Typography>
      </Box>
    : ""}
    followCursor={true}
  >
    <Box
      sx={{position: "absolute", top: "0", left: "0", bottom: "0", right: "0", cursor: "pointer"}}
      onMouseLeave={() => setHoveredIndex && setHoveredIndex(undefined)}
      onClick={() => props.onClick && props.onClick(hoveredIndex || 0)}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left,
          xPct = x / rect.width,
          index = Math.round(xPct * (xValues.length - 1));
        if (index >= 0 && index < xValues.length) {
          setHoveredIndex && setHoveredIndex(index);
        }
      }} >
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${contentArea.width} ${contentArea.height}`}
      preserveAspectRatio='none'
      style={{position: "absolute", top: 0, left: 0, right: 0, bottom: 0}}
      >
      {pathData &&
        <>
        <path vectorEffect="non-scaling-stroke" style={{...areaStyle, fill: topColor}} d={pathData?.upperArea}></path>
        <path vectorEffect="non-scaling-stroke" style={{...areaStyle, fill: bottomColor}} d={pathData?.lowerArea}></path>
        </>
      }
      {pathData &&
        <>
          <path vectorEffect="non-scaling-stroke" style={{...pathStyle, stroke: topColor}} d={pathData?.upperPath}></path>
          <path vectorEffect="non-scaling-stroke" style={{...pathStyle, stroke: bottomColor}} d={pathData?.lowerPath}></path>
        </>
      }
      <line vectorEffect="non-scaling-stroke" x1={0} x2={contentArea.width} y1={axisAreaHeight} y2={axisAreaHeight} style={{...pathStyle, stroke: theme.palette.divider}}/>
      {(hoveredValues && hoveredValues?.hoveredLineX !== undefined) &&
        <line
          vectorEffect="non-scaling-stroke"
          x1={hoveredValues?.hoveredLineX} x2={hoveredValues?.hoveredLineX}
          y1={0} y2={contentArea.height}
          style={{...pathStyle, strokeWidth: "3px", stroke: theme.palette.divider}}/>
      }
    </svg>
    {(true || drawCircles) &&
      <>
      {pointCircles?.map((point, index) => (
        <div
        key={index}
        style={{
          position: "absolute",
          top: `${point.y}%`,
          left: `${point.x}%`,
          width: "8px", height: "8px",
          backgroundColor: theme.palette.background.paper,
          borderRadius: "50%",
          border: `${index === hoveredIndex ? 4 : 2}px solid ${point.color}`,
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}></div>
      ))}
      </>
    }
    </Box>
  </Tooltip>
  );
}